/**
 * FairFare design tokens -- the steerage app's look, as values.
 *
 * Taken from steerage-platform's `wwwroot/css/steerage-v2.css` (its `:root`
 * variables, `.btn`, `.table` and `.sidebar-link` rules) so the two apps read
 * as one product. This dashboard runs two Material UI versions side by side
 * (v5 `@mui/material` and v4 `@material-ui/core`), each with its own theme;
 * both themes are built from this file, so a colour or radius changes here
 * and nowhere else.
 */

export const fonts = {
  // Poppins is the steerage app's face; the rest is its own fallback stack.
  family:
    '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  googleFontsHref:
    'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
};

export const colors = {
  // Surfaces and text (Tailwind slate)
  bg: '#f8fafc', // slate-50  -- page background
  surface: '#ffffff', // cards, tables, sidebar
  border: '#e2e8f0', // slate-200 -- every hairline
  borderStrong: '#cbd5e1', // slate-300 -- hover on secondary buttons
  muted: '#94a3b8', // slate-400 -- placeholders, disabled
  subtle: '#475569', // slate-600 -- body text, table headers
  secondary: '#334155', // slate-700 -- sidebar links
  text: '#0f172a', // slate-900 -- headings, table cells

  // The one accent (Tailwind indigo)
  primary: '#4f46e5', // indigo-600
  primaryDark: '#4338ca', // indigo-700 -- hover
  primaryLight: '#6366f1', // indigo-500
  primarySoft: '#eef2ff', // indigo-50  -- row hover, soft fills
  primaryHover: 'rgba(79,70,229,.08)', // sidebar link hover
  primarySelected: 'rgba(79,70,229,.13)', // sidebar link active

  // Meaning-carrying colours
  success: '#16a34a', // green-600 -- approve
  danger: '#dc2626', // red-600   -- reject, errors
  warning: '#ca8a04', // yellow-600
  info: '#0284c7', // sky-600
};

// In pixels, for the themes' number-based APIs.
export const radius = {
  sm: 8, // .5rem  -- buttons, inputs, sidebar links
  md: 12, // .75rem -- soft cards, menus
  lg: 16, // 1rem   -- cards, panels
};

export const shadows = {
  sm: '0 1px 2px rgba(0,0,0,.05)',
  md: '0 4px 12px rgba(0,0,0,.08)',
  lg: '0 10px 25px rgba(0,0,0,.12)',
};

export const table = {
  headBg: colors.bg,
  headColor: colors.secondary, // slate-700, as steerage's table headers read on screen
  headFontSize: '0.75rem',
  headLetterSpacing: '.06em',
  cellColor: colors.text,
  stripeBg: colors.bg, // even rows
  hoverBg: colors.primarySoft,
};

export const sidebar = {
  width: 256, // 16rem
  linkColor: colors.secondary,
  iconColor: colors.primary,
  linkHoverBg: colors.primaryHover,
  linkActiveBg: colors.primarySelected,
  linkActiveColor: colors.primary,
  // 14px: at 15px the longest labels ("Medical Records Update") overran the rows.
  fontSize: '0.875rem',
};

const tokens = { fonts, colors, radius, shadows, table, sidebar };
export default tokens;
